import { useEffect, useMemo, useRef, useState } from 'react'
import { Info, Share2, ShoppingCart, Store } from 'lucide-react'
import { useParams, useSearchParams } from 'react-router-dom'
import PublicCatalogProductItem from './components/PublicCatalogProductItem'
import PublicCatalogCart from './components/PublicCatalogCart'
import PublicCatalogProductModal from './components/PublicCatalogProductModal'
import PublicCatalogFilterPanel from './components/PublicCatalogFilterPanel'
import PublicCatalogInfoModal from './components/PublicCatalogInfoModal'
import { entrepreneurCatalogService } from '../../services/entrepreneurCatalogService'

const getCartStorageKey = (slug) => `entrepreneur-catalog-cart:/catalogo/${slug || ''}`
const headerIconButtonClass = 'flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-700 shadow-sm shadow-slate-200/70 transition hover:-translate-y-0.5 hover:border-slate-300 hover:bg-white hover:text-slate-950 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[var(--catalog-primary)]/25 sm:h-10 sm:w-10'

const loadCart = (slug) => {
  try {
    const stored = window.sessionStorage.getItem(getCartStorageKey(slug))
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

export default function PublicCatalog() {
  const { slug } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [catalog, setCatalog] = useState(null)
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '')
  const pageSize = Number(searchParams.get('pageSize') || 24)
  const [cartOpen, setCartOpen] = useState(false)
  const [cartItems, setCartItems] = useState(() => loadCart(slug))
  const [selectedProductData, setSelectedProductData] = useState(null)
  const [selectedVideoId, setSelectedVideoId] = useState(null)
  const [selectedQuantity, setSelectedQuantity] = useState(1)
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)
  const [selectedCategories, setSelectedCategories] = useState([])
  const [infoOpen, setInfoOpen] = useState(false)
  const hasLoadedCatalogRef = useRef(false)
  const requestIdRef = useRef(0)

  const appliedQuery = searchParams.get('q') || ''
  const page = Number(searchParams.get('page') || 1)

  useEffect(() => {
    setCartItems(loadCart(slug))
  }, [slug])

  useEffect(() => {
    hasLoadedCatalogRef.current = false
    setCatalog(null)
    setLoading(true)
    setRefreshing(false)
  }, [slug])

  useEffect(() => {
    try {
      window.sessionStorage.setItem(getCartStorageKey(slug), JSON.stringify(cartItems))
    } catch {}
  }, [cartItems, slug])

  useEffect(() => {
    let cancelled = false
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    const controller = new AbortController()
    const load = async () => {
      const hasPreviousCatalog = hasLoadedCatalogRef.current
      setLoading(!hasPreviousCatalog)
      setRefreshing(hasPreviousCatalog)
      try {
        const data = await entrepreneurCatalogService.getPublicCatalog(slug, appliedQuery, page, pageSize, { signal: controller.signal })
        if (!cancelled && requestId === requestIdRef.current) {
          setCatalog(data)
          hasLoadedCatalogRef.current = true
        }
      } catch (error) {
        if (!cancelled && error.name !== 'AbortError' && !hasLoadedCatalogRef.current) {
          setCatalog(null)
        }
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setLoading(false)
          setRefreshing(false)
        }
      }
    }

    load()
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [slug, appliedQuery, page, pageSize])

  useEffect(() => {
    setSearchInput(appliedQuery)
  }, [appliedQuery])

  const totalCartQuantity = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems]
  )

  const filteredItems = useMemo(() => {
    if (!catalog?.items) return []
    if (selectedCategories.length === 0) return catalog.items
    return catalog.items.filter((product) => selectedCategories.includes(product.categoryName))
  }, [catalog?.items, selectedCategories])

  const updateSearch = (next) => {
    const params = new URLSearchParams(searchParams)
    if (next.trim()) params.set('q', next.trim())
    else params.delete('q')
    params.delete('page')
    setSearchParams(params)
    if (window.innerWidth < 768) {
      setFilterPanelOpen(false)
    }
  }

  const toggleCategory = (categoryName) => {
    setSelectedCategories((current) =>
      current.includes(categoryName)
        ? current.filter((c) => c !== categoryName)
        : [...current, categoryName]
    )
  }

  const updatePage = (nextPage) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', String(nextPage))
    setSearchParams(params)
  }

  const addToCart = (product, quantity = 1) => {
    const qty = Math.max(1, Number(quantity || 1))
    setCartItems((current) => {
      const existing = current.find((item) => item.id === product.id)
      if (existing) {
        return current.map((item) => item.id === product.id ? { ...item, quantity: item.quantity + qty } : item)
      }

      return [
        ...current,
        {
          id: product.id,
          name: product.name,
          slug: product.slug,
          categoryName: product.categoryName || '',
          thumbnailUrl: product.thumbnailUrl || '',
          price: Number(product.price || 0),
          quantity: qty
        }
      ]
    })
    setCartOpen(true)
  }

  const changeCartQuantity = (productId, nextQuantity) => {
    setCartItems((current) => {
      if (nextQuantity <= 0) {
        return current.filter((item) => item.id !== productId)
      }

      return current.map((item) => item.id === productId ? { ...item, quantity: nextQuantity } : item)
    })
  }

  const openProductModal = async (product) => {
    try {
      const result = await entrepreneurCatalogService.getPublicProduct(slug, product.id)
      const primary = result?.product?.videos?.find((video) => video.isPrimary) || result?.product?.videos?.[0] || null
      setSelectedProductData(result)
      setSelectedVideoId(primary?.id || null)
      setSelectedQuantity(1)
    } catch {
      setSelectedProductData(null)
    }
  }

  const addSelectedProductToCart = () => {
    if (!selectedProductData?.product) return
    const selectedVideo = selectedProductData.product.videos.find((video) => video.id === selectedVideoId) || selectedProductData.product.videos[0] || null
    addToCart({
      ...selectedProductData.product,
      thumbnailUrl: selectedVideo?.thumbnailUrl || ''
    }, selectedQuantity)
  }

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-slate-600">Cargando catalogo...</div>
  }

  if (!catalog) {
    return <div className="min-h-screen grid place-items-center text-slate-600">Catalogo no encontrado o no publicado.</div>
  }

  return (
    <div
      className="min-h-screen bg-slate-100 text-slate-900"
      style={{
        '--catalog-primary': catalog.profile.primaryColor || '#2563eb',
        '--catalog-secondary': catalog.profile.secondaryColor || '#1d4ed8'
      }}
    >
      <section className="sticky top-0 z-10 border-b border-slate-200 bg-white/90 backdrop-blur-xl px-3 py-3 sm:px-5 md:px-6 md:py-4">
        <div className="grid grid-cols-[52px_minmax(0,1fr)] items-center gap-x-3 gap-y-2 md:grid-cols-[56px_minmax(0,1fr)_auto] md:gap-x-4">
          <div
            className="row-span-2 flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-2xl bg-white shadow-lg shadow-slate-300/40 ring-1 ring-white/60 md:row-span-1 md:h-14 md:w-14 md:rounded-[18px]"
            style={{ background: `linear-gradient(145deg, ${catalog.profile.primaryColor || '#2563eb'}, #60a5fa)` }}
          >
            <Store className="h-7 w-7 text-white md:h-8 md:w-8" strokeWidth={2.2} aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <div className="mb-0.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 md:mb-1 md:text-[11px]">Catalogo Emprendedor</div>
            <h1 className="truncate text-lg font-semibold leading-tight text-slate-950 sm:text-xl md:text-[clamp(22px,3vw,34px)]">
              {catalog.profile.displayName || catalog.profile.businessName}
            </h1>
            <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-slate-500 sm:text-sm md:mt-1">
              {catalog.profile.tagline || catalog.profile.description || 'Explora el catalogo y descubre productos en video.'}
            </p>
          </div>

          <div className="col-start-2 flex items-center justify-end gap-2 md:col-start-3 md:row-start-1">
            <button
              type="button"
              onClick={() => setInfoOpen(true)}
              className={headerIconButtonClass}
              aria-label="Informacion"
              title="Informacion"
            >
              <Info className="h-[18px] w-[18px] sm:h-5 sm:w-5" strokeWidth={2.2} aria-hidden="true" />
            </button>

            <button
              type="button"
              onClick={async () => {
                const url = `${window.location.origin}/catalogo/${catalog.profile.slug}`
                if (navigator.share) {
                  try {
                    await navigator.share({ title: catalog.profile.displayName || catalog.profile.businessName, url })
                  } catch {}
                } else {
                  try {
                    await navigator.clipboard.writeText(url)
                    alert('Enlace copiado al portapapeles')
                  } catch {}
                }
              }}
              className={headerIconButtonClass}
              aria-label="Compartir"
              title="Compartir"
            >
              <Share2 className="h-[18px] w-[18px] sm:h-5 sm:w-5" strokeWidth={2.2} aria-hidden="true" />
            </button>

            <button
              type="button"
              onClick={() => setCartOpen((current) => !current)}
              className={`${headerIconButtonClass} relative`}
              aria-label="Carrito"
              title="Carrito"
            >
              <ShoppingCart className="h-[18px] w-[18px] sm:h-5 sm:w-5" strokeWidth={2.2} aria-hidden="true" />
              {totalCartQuantity > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[var(--catalog-primary)] px-1.5 text-[11px] font-bold leading-none text-white ring-2 ring-white">
                  {totalCartQuantity}
                </span>
              )}
            </button>
          </div>
        </div>
      </section>

       <main className="px-[3px] md:px-3 grid gap-3 w-full">
        <div className="md:hidden">
          <PublicCatalogFilterPanel
            products={catalog.items}
            totalCount={filteredItems.length}
            selectedCategories={selectedCategories}
            onCategoryChange={toggleCategory}
            isOpen={filterPanelOpen}
            onToggle={() => setFilterPanelOpen((current) => !current)}
            searchValue={searchInput}
            onSearchChange={setSearchInput}
            onSearchSubmit={() => updateSearch(searchInput)}
            slug={catalog.profile.slug}
            isSearching={refreshing}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-3">
          <aside className="hidden md:block">
            <div className="sticky top-[120px]">
              <PublicCatalogFilterPanel
                products={catalog.items}
                totalCount={filteredItems.length}
                selectedCategories={selectedCategories}
                onCategoryChange={toggleCategory}
                isOpen={true}
                onToggle={() => {}}
                searchValue={searchInput}
                onSearchChange={setSearchInput}
                onSearchSubmit={() => updateSearch(searchInput)}
                slug={catalog.profile.slug}
                isSearching={refreshing}
              />
            </div>
          </aside>

          {filteredItems.length ? (
            <section className="relative grid grid-cols-1 md:grid-cols-[repeat(auto-fill,minmax(220px,220px))] gap-3 w-full">
              {refreshing && (
                <div className="pointer-events-none absolute inset-x-0 top-0 z-[1] flex justify-center">
                  <div className="rounded-full border border-slate-200 bg-white/95 px-3 py-1 text-xs font-semibold text-slate-500 shadow-sm">
                    Buscando productos...
                  </div>
                </div>
              )}
              {filteredItems.map((product) => (
                <PublicCatalogProductItem
                  key={product.id}
                  product={product}
                  onAddToCart={addToCart}
                  onOpenDetails={openProductModal}
                />
              ))}
            </section>
          ) : (
            <div className="min-h-[320px] rounded-3xl border border-dashed border-slate-300 bg-white/75 grid place-items-center text-center text-slate-500 p-6">
              No hay productos para esta busqueda.
            </div>
          )}

        </div>

        <div className="flex justify-center flex-wrap gap-2 py-2">
          {Array.from({ length: catalog.totalPages }, (_, index) => index + 1).slice(0, 12).map((itemPage) => (
            <button
              key={itemPage}
              type="button"
              onClick={() => updatePage(itemPage)}
              className={`min-w-[42px] h-[42px] rounded-xl border ${itemPage === catalog.page ? 'bg-blue-600 text-white border-transparent' : 'bg-white border-slate-200 text-slate-700'}`}
            >
              {itemPage}
            </button>
          ))}
        </div>
      </main>

      <PublicCatalogCart
        slug={catalog.profile.slug}
        contactPhone={catalog.profile.contactPhone || ''}
        contactName={catalog.profile.displayName || catalog.profile.businessName || ''}
        isOpen={cartOpen}
        items={cartItems}
        onClose={() => setCartOpen(false)}
        onChangeQuantity={changeCartQuantity}
        onClear={() => setCartItems([])}
      />

      <PublicCatalogProductModal
        isOpen={Boolean(selectedProductData)}
        productData={selectedProductData}
        selectedVideoId={selectedVideoId}
        selectedQuantity={selectedQuantity}
        totalCartQuantity={totalCartQuantity}
        onClose={() => setSelectedProductData(null)}
        onSelectVideo={setSelectedVideoId}
        onDecreaseQuantity={() => setSelectedQuantity((current) => Math.max(1, current - 1))}
        onIncreaseQuantity={() => setSelectedQuantity((current) => current + 1)}
        onAddToCart={addSelectedProductToCart}
        onToggleCart={() => setCartOpen((current) => !current)}
      />

      <PublicCatalogInfoModal
        isOpen={infoOpen}
        onClose={() => setInfoOpen(false)}
        profile={catalog.profile}
      />
    </div>
  )
}
